import React, { useState, useCallback, useRef } from 'react';
import type {
  UseFormGetValues,
  UseFormTrigger,
  UseFormReset,
  Control,
} from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import type { IntakeResponse } from '../../services/api';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import type { CreatePatientFormData, SaveStatus } from './types';
import { LANGUAGE_OPTIONS, OTHER_LANGUAGE_KEY } from './constants';

interface UseFormAutoSaveOptions {
  intakeId: string | undefined;
  selectedIntake: IntakeRecord | null;
  updateIntakeRecord: (
    id: string,
    data: Partial<IntakeResponse>
  ) => Promise<IntakeRecord>;
  control: Control<CreatePatientFormData>;
  getValues: UseFormGetValues<CreatePatientFormData>;
  trigger: UseFormTrigger<CreatePatientFormData>;
  reset: UseFormReset<CreatePatientFormData>;
}

interface UseFormAutoSaveReturn {
  saveStatus: SaveStatus;
  saveToServer: (updates: Partial<CreatePatientFormData>) => Promise<void>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
}

/**
 * Hook for auto-saving form data on field blur.
 * After successful save, resets form baseline so isDirty compares against last saved values.
 */
export function useFormAutoSave({
  intakeId,
  selectedIntake,
  updateIntakeRecord,
  control,
  getValues,
  trigger,
  reset,
}: UseFormAutoSaveOptions): UseFormAutoSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { dirtyFields } = useFormState({ control });

  // Use ref to access latest dirtyFields in callbacks (avoids stale closure)
  const dirtyFieldsRef = useRef(dirtyFields);
  dirtyFieldsRef.current = dirtyFields;

  // Tracks in-flight saveToServer calls per field key using reference counts.
  // Prevents a blur-triggered save from racing against an onChange-triggered save that
  // already includes the correct full patch (e.g. kinnser_visit_type + kinnser_creation_type).
  // Reference counting (vs a plain Set) handles concurrent saves sharing the same key:
  // the key stays tracked until every overlapping save completes.
  const savingFieldsRef = useRef<Map<string, number>>(new Map());

  const saveToServer = useCallback(
    async (updates: Partial<CreatePatientFormData>) => {
      if (!intakeId || !selectedIntake) return;

      const fieldKeys = Object.keys(updates);
      fieldKeys.forEach(k =>
        savingFieldsRef.current.set(
          k,
          (savingFieldsRef.current.get(k) ?? 0) + 1
        )
      );

      try {
        setSaveStatus('saving');
        // Send only the changed fields
        await updateIntakeRecord(intakeId, updates as Partial<IntakeResponse>);

        // Reset form baseline to current values so isDirty compares against what was just saved
        const currentValues = getValues();
        reset(currentValues, { keepValues: true, keepErrors: true });

        setSaveStatus('saved');

        // Clear previous timeout and set new one
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (error) {
        console.error('Error saving intake:', error);
        setSaveStatus('error');

        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 3000);
      } finally {
        fieldKeys.forEach(k => {
          const count = (savingFieldsRef.current.get(k) ?? 1) - 1;
          if (count <= 0) {
            savingFieldsRef.current.delete(k);
          } else {
            savingFieldsRef.current.set(k, count);
          }
        });
      }
    },
    [intakeId, selectedIntake, updateIntakeRecord, getValues, reset]
  );

  const handleFieldBlur = useCallback(
    (fieldEvent: React.FocusEvent<HTMLElement>) => {
      const target = fieldEvent.target as HTMLInputElement;
      // Try name attribute first, fall back to data-field-name attribute
      const fieldName = (target.name ||
        target.dataset.fieldName) as keyof CreatePatientFormData;

      if (!fieldName) {
        console.warn(
          'handleFieldBlur: Could not determine field name',
          fieldName
        );
        return;
      }

      // Use setTimeout to defer work so the blur event completes immediately (dropdown closes)
      setTimeout(async () => {
        // Skip save if field hasn't changed (use ref for latest value)
        if (!dirtyFieldsRef.current[fieldName]) {
          return;
        }

        // Skip save if this field is already being saved via an explicit saveToServer call
        // (e.g. visit type onChange saves kinnser_visit_type + kinnser_creation_type together;
        // without this guard the blur would fire a second save with only kinnser_visit_type,
        // dropping kinnser_creation_type and causing a race condition).
        if (savingFieldsRef.current.has(fieldName)) {
          return;
        }

        const isFieldValid = await trigger(fieldName);
        if (!isFieldValid) {
          return;
        }

        const fieldValue = getValues(fieldName);

        // Handle special case for language field - update patient_primary_language
        const updates: Partial<CreatePatientFormData> = {
          [fieldName]: fieldValue,
        };

        if (fieldName === 'kinnser_language_key') {
          const languageKey = fieldValue as string;
          let patientPrimaryLanguage: string | null = null;

          if (languageKey && languageKey !== OTHER_LANGUAGE_KEY) {
            // Find the language label from the options
            const languageOption = LANGUAGE_OPTIONS.find(
              option => option.value === languageKey
            );
            patientPrimaryLanguage = languageOption?.label || null;
          } else if (languageKey === OTHER_LANGUAGE_KEY) {
            // Use the content of kinnser_other_language field
            const otherLanguage = getValues('kinnser_other_language');
            patientPrimaryLanguage = otherLanguage || null;
          }

          updates.patient_primary_language = patientPrimaryLanguage;
        }

        if (
          fieldName === 'kinnser_other_language' &&
          getValues('kinnser_language_key') === OTHER_LANGUAGE_KEY
        ) {
          const otherLanguage = fieldValue as string;
          updates.patient_primary_language = otherLanguage;
        }

        // When case manager changes, also sync approver fields to the same value
        if (fieldName === 'kinnser_case_manager_id' && fieldValue) {
          updates.kinnser_oasis_approver_id = fieldValue as string;
          updates.kinnser_pt_approver_id = fieldValue as string;
          updates.kinnser_ot_approver_id = fieldValue as string;
          updates.kinnser_st_approver_id = fieldValue as string;
        }

        // Save the changed fields
        saveToServer(updates);
      }, 0);
    },
    [trigger, getValues, saveToServer]
  );

  return {
    saveStatus,
    saveToServer,
    handleFieldBlur,
  };
}
